import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { toast } from "react-toastify";
import { requestAuth } from "./RequestService";
import { Button } from "../Buttons";

export const ConfirmationLDocsForm = ({ data, dialogClose, procedure }) => {
  const [leftDocs, setLeftDocs] = useState(
    data.map((d, i) => ({
      id: (d.order ?? i).toString(),
      label: d.label,
      description: d.description || null,
      dataset: d.dataSetId,
      original: d,
    }))
  );

  const [rightDocs, setRightDocs] = useState(
    data.map((d, i) => ({
      id: `url-${i}`,
      url: d.description || null,
      originId: d.url ? (d.order ?? i).toString() : null,
      checked: !!d.description, 
    }))
  );

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (source.droppableId === "right" && destination.droppableId === "right") {
      const newRight = Array.from(rightDocs);
      const [removed] = newRight.splice(source.index, 1);
      newRight.splice(destination.index, 0, removed);
      setRightDocs(newRight);
    }

    if (source.droppableId === "right" && destination.droppableId === "left") {
      const dragged = rightDocs[source.index];
      if (!dragged || !dragged.url) return;

      const newLeftDocs = leftDocs.map((doc) => {
        if (doc.id === dragged.originId) {
          return { ...doc, description: null };
        }
        if (doc.id === leftDocs[destination.index].id) {
          return { ...doc, description: dragged.url };
        }
        return doc;
      });

      const newRightDocs = rightDocs.map((r, i) =>
        i === source.index ? { ...r, url: null, originId: null } : r
      );

      setLeftDocs(newLeftDocs);
      setRightDocs(newRightDocs);
    }
  };  
  // Función para Desmarcar (desplazar hacia abajo) o Marcar (desplazar hacia arriba)
  const handleCheckIndividual = (index) => {
    const newRightDocs = [...rightDocs];
    const currentDoc = newRightDocs[index];

    if (currentDoc.checked) {
      // ACCIÓN: DESMARCAR (Crear un vacío aquí)
      // Buscamos el primer espacio vacío que esté DEBAJO de la casilla clickeada
      let emptyIndex = -1;
      for (let j = index + 1; j < newRightDocs.length; j++) {
        if (!newRightDocs[j].url) {
          emptyIndex = j;
          break;
        }
      }

      if (emptyIndex !== -1) {
        // 1. Encontramos el vacío abajo. Hacemos que todos bajen un paso (ej. 4,5,6,7 bajan a 5,6,7,8)
        for (let k = emptyIndex; k > index; k--) {
          newRightDocs[k].url = newRightDocs[k - 1].url;
          newRightDocs[k].originId = newRightDocs[k - 1].originId;
          newRightDocs[k].checked = newRightDocs[k - 1].checked;
        }
        // 2. La casilla clickeada (ej. 3) queda vacía
        currentDoc.url = null;
        currentDoc.originId = null;
        currentDoc.checked = false;
      } else {
        // Opcional: Si el vacío resulta que estaba ARRIBA, hacemos que todos suban un paso
        let emptyIndexAbove = -1;
        for (let j = index - 1; j >= 0; j--) {
          if (!newRightDocs[j].url) {
            emptyIndexAbove = j;
            break;
          }
        }
        
        if (emptyIndexAbove !== -1) {
          for (let k = emptyIndexAbove; k < index; k++) {
            newRightDocs[k].url = newRightDocs[k + 1].url;
            newRightDocs[k].originId = newRightDocs[k + 1].originId;
            newRightDocs[k].checked = newRightDocs[k + 1].checked;
          }
          currentDoc.url = null;
          currentDoc.originId = null;
          currentDoc.checked = false;
        } else {
          // Si no hay vacíos en toda la tabla, solo la limpiamos
          currentDoc.url = null;
          currentDoc.originId = null;
          currentDoc.checked = false;
        }
      }
    } else {
      // ACCIÓN: MARCAR (Rellenar este vacío)
      // Jalamos todos los enlaces que están debajo, un paso hacia arriba
      for (let k = index; k < newRightDocs.length - 1; k++) {
        newRightDocs[k].url = newRightDocs[k + 1].url;
        newRightDocs[k].originId = newRightDocs[k + 1].originId;
        newRightDocs[k].checked = !!newRightDocs[k + 1].url;
      }

      // Como todo subió un paso, la última fila de la tabla queda obligatoriamente vacía
      const lastIndex = newRightDocs.length - 1;
      newRightDocs[lastIndex].url = null;
      newRightDocs[lastIndex].originId = null;
      newRightDocs[lastIndex].checked = false;
    }

    setRightDocs(newRightDocs);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const updatedDocs = leftDocs.map((doc, index) => ({
      ...doc,
      description: rightDocs[index]?.url || null,
    }));

    try {
      await requestAuth("patch", `/dataSet/subData/confirmationLDocs`, updatedDocs);
      toast.success("Guardado correctamente.");
      dialogClose();
    } catch (error) {
      toast.error("Se produjo un error al confirmar el orden de los documentos.");
    }
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-4 mb-5 bg-gray-50 rounded-lg overflow-hidden justify-center w-full">
        <div><h2 className="font-semibold text-center uppercase">Confirmación de documentos</h2></div>
        <div><span className="text-sm">Es posible que haya un desfase en el trámite <span className="font-bold text-red-700">{procedure?.numRef}</span> al extraer los documentos. <br />
          Por favor, confirme el orden de los enlaces según la sección L:</span></div>
        <div className="flex w-full gap-2 overflow-auto">
          <Droppable droppableId="left">
            {(provided, snapshot) => (
              <table
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`border-collapse border border-gray-300 rounded-lg shadow-sm bg-white transition-all ${snapshot.isDraggingOver ? 'ring-2 ring-blue-400 bg-blue-50' : ''
                  }`}
              >
                <thead>
                  <tr>
                    <th className="border border-gray-300 bg-[#1c3144] text-white px-4 py-2 font-semibold">Nombre documento</th>
                    <th className="border border-gray-300 bg-[#1c3144] text-white px-4 py-2 font-semibold">URL</th>
                  </tr>
                </thead>
                <tbody>
                  {leftDocs.map((doc, index) => (
                    <Draggable
                      key={doc.id.toString()}
                      draggableId={doc.id.toString()}
                      index={index}
                      isDragDisabled
                    >
                      {(provided) => (
                        <tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="border border-gray-300 px-4 py-2 font-medium text-gray-800 max-w-[300px] truncate">
                            {doc.label}
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            {doc.description ? (
                              <a
                                href={doc.description}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
                              >
                                🔗 Ver
                              </a>
                            ) : (
                              <span className="text-gray-400 italic">(vacio)</span>
                            )}
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              </table>
            )}
          </Droppable>

          <Droppable droppableId="right">
            {(provided, snapshot) => (
              <table
                ref={provided.innerRef}
                {...provided.droppableProps}
                className={`border-collapse border border-gray-300 rounded-lg shadow-sm bg-white transition-all ${snapshot.isDraggingOver ? 'ring-2 ring-[#ffe99a] bg-[#fffbeb]' : ''
                  }`}
              >
                <thead>
                  <tr>
                    <th className="border border-gray-300 bg-[#FBCE00] px-4 py-2 font-semibold">URLs disponibles</th>
                    
                    <th className="border border-gray-300 bg-[#FBCE00] px-4 py-2 font-semibold text-center w-[130px]">
                      Estado
                    </th>

                  </tr>
                </thead>
                <tbody>
                  {rightDocs.map((doc, index) => (
                    <Draggable key={doc.id} draggableId={doc.id} index={index}>
                      {(provided, snapshot) => (
                        <tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`${snapshot.isDragging ? 'bg-blue-100 shadow-md' : 'hover:bg-gray-50'
                            } ${!doc.url ? 'opacity-60' : 'cursor-grab'}`}
                        >
                          <td className="border border-gray-300 px-2 py-2 max-w-[180px]">
                            {doc.url ? (
                              <a
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#745a00] hover:text-[#d4a600] font-medium underline transition-colors truncate block"
                              >
                                🔗 Enlace de documento
                              </a>
                            ) : (
                              <span className="text-gray-400 italic">Sin URL</span>
                            )}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-center">
                            <input 
                              type="checkbox" 
                              checked={doc.checked} 
                              onChange={() => handleCheckIndividual(index)} 
                              className="w-4 h-4 cursor-pointer"
                            />
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              </table>
            )}
          </Droppable>
        </div>
        <div className="flex justify-end pt-2">
          <Button type="submit" className="w-full">
            Guardar cambios
          </Button>
        </div>
      </form>
    </DragDropContext>
  );
};